import currency from 'currency.js'

import { CURRENCY_SYMBOLS } from '@layers/web/constants/currency.ts'
import { convertSekAmountsInText, sekToEur } from '@layers/web/utils/currency.ts'

type MoneyFormat = {
  money: (value: string | number, options?: object) => currency
}

type MoneyOptions = currency.Options & {
  market?: string
}

const CURRENCY_FORMATS: Record<string, MoneyFormat> = {
  sv: {
    money: (
      value: string | number,
      options = {}
    ) => currency(
      value,
      {
        symbol: CURRENCY_SYMBOLS.sv,
        precision: 0,
        separator: '\u2009',
        decimal: ',',
        pattern: '#!',
        negativePattern: '-#!',
        ...options,
      }
    ),
  },

  pl: {
    money: (
      value: string | number,
      options = {}
    ) => currency(
      value,
      {
        symbol: CURRENCY_SYMBOLS.pl,
        precision: 0,
        separator: '\u2009',
        decimal: ',',
        pattern: '#!',
        negativePattern: '-#!',
        ...options,
      }
    ),
  },

  no: {
    money: (
      value: string | number,
      options = {}
    ) => currency(
      value,
      {
        symbol: CURRENCY_SYMBOLS.no,
        precision: 0,
        separator: '\u2009',
        decimal: ',',
        pattern: '#!',
        negativePattern: '-#!',
        ...options,
      }
    ),
  },

  'zh-cn': {
    money: (
      value: string | number,
      options = {}
    ) => currency(
      value,
      {
        symbol: CURRENCY_SYMBOLS['zh-cn'],
        precision: 0,
        separator: ',',
        decimal: '.',
        pattern: '!#',
        negativePattern: '-!#',
        ...options,
      }
    ),
  },

  en: {
    money: (
      value: string | number,
      options = {}
    ) => currency(
      value,
      {
        symbol: CURRENCY_SYMBOLS.en,
        precision: 0,
        separator: ',',
        decimal: '.',
        pattern: '!#',
        negativePattern: '-!#',
        ...options,
      }
    ),
  },

  eur: {
    money: (
      value: string | number,
      options = {}
    ) => currency(
      value,
      {
        symbol: CURRENCY_SYMBOLS.eur,
        precision: 0,
        separator: ' ',
        decimal: ',',
        pattern: '!#',
        negativePattern: '-!#',
        ...options,
      }
    ),
  },
}

export default defineNuxtPlugin(() => {
  const { public: { showEurPricing, market: siteMarket } } = useRuntimeConfig()

  const n = (number: string | number, options: MoneyOptions = {}) => {
    const { market, ...formatOptions } = options
    const value = typeof number === 'number' ? number : parseFloat(number)
    const currencyKey = market ?? (showEurPricing ? 'eur' : siteMarket)
    const currencyFormat = CURRENCY_FORMATS[currencyKey] || CURRENCY_FORMATS.sv
    const amount = currencyKey === 'eur' ? sekToEur(value) : value

    return currencyFormat.money(amount, formatOptions).format()
  }

  return {
    provide: {
      n,

      priceText: (text: string) =>
        showEurPricing && text ? convertSekAmountsInText(text, n) : text,

      currency: (value: number, options: object) =>
        currency(value, { ...options, precision: 0, separator: '', pattern: '# !', negativePattern: '-# !' }),
    },
  }
})